import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { companyApi } from "../apis/company/api";
import { loadTypesApi } from "../apis/loadTypes/api";
import { FetchLoadTypedData } from "../apis/loadTypes/types";
import { mapboxApi } from "../apis/mapbox/api";
import { marketsApi } from "../apis/markets/api";
import { FetchMarketsData } from "../apis/markets/types";
import { settingsApi } from "../apis/settings/api";
import { userApi } from "../apis/user/api";
import LoadingSpinner from "../components/common/LoadingSpinner/LoadingSpinner";
import { CompanyDetails } from "../types/company";
import { LoadingTypesEnum } from "../types/loading";
import { MapBoxConfig } from "../types/mapbox";
import { GlobalSetting, PersonalSetting } from "../types/settings";
import { User } from "../types/user";
import { generateAppInstanceId } from "../utils/appInstance";
import { useUserSessionContext } from "./UserSessionContext";

interface RequiredDataState {
  loading: LoadingTypesEnum;
  appInstanceId: string;
  myProfile: User | null;
  globalSettings: GlobalSetting[] | null;
  personalSettings: PersonalSetting[] | null;
  companyDetails: CompanyDetails | null;
  loadTypes: FetchLoadTypedData | null;
  marketsOperated: FetchMarketsData | null;
  mapBoxConfig: MapBoxConfig | null;
}

const defaultRequiredDataState: RequiredDataState = {
  loading: LoadingTypesEnum.idle,
  appInstanceId: generateAppInstanceId(),
  myProfile: null,
  globalSettings: null,
  personalSettings: null,
  companyDetails: null,
  loadTypes: null,
  marketsOperated: null,
  mapBoxConfig: null,
};

const RequiredDataContext = createContext<RequiredDataState | null>(null);

const RequiredDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<RequiredDataState>(
    defaultRequiredDataState,
  );
  const { isLoggedIn } = useUserSessionContext();

  useEffect(() => {
    const fetchRequiredData = async () => {
      setState((prev) => ({ ...prev, loading: LoadingTypesEnum.pending }));

      if (!isLoggedIn) {
        setState(defaultRequiredDataState);
        return;
      }

      try {
        const [
          myProfile,
          globalSettings,
          personalSettings,
          companyDetails,
          loadTypes,
          marketsOperated,
          mapBoxConfig,
        ] = await Promise.all([
          userApi.getMyProfile(),
          settingsApi.getGlobalSettings(),
          settingsApi.getPersonalSettings(),
          companyApi.getCompanyDetails(),
          loadTypesApi.getLoadTypes(),
          marketsApi.getMarketsOperated(),
          mapboxApi.getMapboxConfiguration(),
        ]);

        setState((prev) => ({
          ...prev,
          myProfile,
          globalSettings,
          personalSettings,
          companyDetails,
          loadTypes,
          marketsOperated,
          mapBoxConfig,
          loading: LoadingTypesEnum.fulfilled,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: LoadingTypesEnum.rejected,
        }));
      }
    };

    fetchRequiredData();
  }, [isLoggedIn]);

  return (
    <RequiredDataContext.Provider value={state}>
      {state.loading === LoadingTypesEnum.pending ? (
        <LoadingSpinner />
      ) : (
        children
      )}
    </RequiredDataContext.Provider>
  );
};

export const useRequiredData = () => {
  const context = useContext(RequiredDataContext);

  if (!context) {
    throw new Error(
      "useRequiredData must be used within a RequiredDataProvider",
    );
  }

  return context;
};

export default RequiredDataProvider;
